import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, TextField, Button, Chip, Stack, Alert, CircularProgress, Divider, Checkbox, FormControlLabel, FormGroup, GlobalStyles } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import BarometerApi, { BarometerWaveSummary, BarometerReport, BarometerModule, BAROMETER_MODULES } from "../api/BarometerApi";

// Print CSS scoped to the opened wave report (same pattern as the self-tests):
// "Afdrukken of bewaren als PDF" prints only the k-safe report block.
const printStyles = (
  <GlobalStyles
    styles={{
      "@media print": {
        "body *": { visibility: "hidden" },
        ".baro-print, .baro-print *": { visibility: "visible" },
        ".baro-print": { position: "absolute", left: 0, top: 0, width: "100%", padding: "24px" },
        ".baro-no-print": { display: "none !important" },
      },
    }}
  />
);

// Org-admin section for the anonymous wellbeing barometer (v2, modular:
// welzijn = Cantril-ladder, burnout = BAT-12, werkbeleving = eigen
// Carefunded-werkitems). Shows k-anonymous aggregates only, PER MODULE: a
// module below k (10) is omitted entirely — deliberately without even a
// response count.
const SUBSCALES = ["uitputting", "mentaleDistantie", "cognitieveOntregeling", "emotioneleOntregeling"] as const;

function delta(current: number, prev: number): string {
  const d = Math.round((current - prev) * 10) / 10;
  return d > 0 ? `+${d}` : `${d}`;
}

function ModuleSuppressedNote({ k }: { k: number }) {
  const { t } = useTranslation();
  return <Alert severity="info">{t("barometer.moduleSuppressed", { k })}</Alert>;
}

function ReportView({ report }: { report: BarometerReport }) {
  const { t } = useTranslation();
  if (report.suppressed) {
    return <Alert severity="info">{t("barometer.suppressedReport", { k: report.kThreshold })}</Alert>;
  }
  const prev = report.previous;
  const modules = report.modules || [];
  return (
    <Stack spacing={2.5}>
      {modules.includes("welzijn") && (
        !report.welzijn ? <ModuleSuppressedNote k={report.kThreshold} /> : (
          <Box sx={{ p: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
            <Typography variant="caption" sx={{ color: "primary.dark", textTransform: "uppercase", letterSpacing: ".05em" }}>{t("barometer.welzijnScore")}</Typography>
            <Typography sx={{ fontSize: "2.2rem", fontWeight: 700, my: 0.5 }}>
              {report.welzijn.nowMean}
              {prev?.welzijn && <Box component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: "text.secondary", ml: 1 }}>({delta(report.welzijn.nowMean, prev.welzijn.nowMean)} {t("barometer.vsPrev", { title: prev.title })})</Box>}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", m: 0 }}>{t("barometer.welzijnHint")}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mt: 1, mb: 0 }}>
              {t("barometer.welzijnFuture")}: <Box component="span" sx={{ fontWeight: 600 }}>{report.welzijn.futureMean}</Box>
              {prev?.welzijn && <Box component="span" sx={{ color: "text.secondary", ml: 1 }}>({delta(report.welzijn.futureMean, prev.welzijn.futureMean)})</Box>}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".85rem", mt: 1, mb: 0 }}>{t("barometer.nLabel", { n: report.welzijn.n })}</Typography>
          </Box>
        )
      )}

      {modules.includes("burnout") && (
        !report.burnout ? <ModuleSuppressedNote k={report.kThreshold} /> : (
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t("barometer.batReportTitle")}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mb: 1.5 }}>{t("barometer.batHint")}</Typography>
            <Stack spacing={1}>
              {SUBSCALES.map((s) => (
                <Box key={s} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ m: 0 }}>{t(`barometer.sub.${s}`)}</Typography>
                  <Typography sx={{ m: 0, fontWeight: 600 }}>
                    {report.burnout!.subscales[s]}
                    {prev?.burnout && <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 1 }}>({delta(report.burnout!.subscales[s], prev.burnout.subscales[s])})</Box>}
                  </Typography>
                </Box>
              ))}
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ m: 0, fontWeight: 600 }}>{t("barometer.batOverall")}</Typography>
                <Typography sx={{ m: 0, fontWeight: 700 }}>
                  {report.burnout.overallMean}
                  {prev?.burnout && <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 1 }}>({delta(report.burnout.overallMean, prev.burnout.overallMean)})</Box>}
                </Typography>
              </Box>
            </Stack>
            <Typography sx={{ color: "text.secondary", fontSize: ".85rem", mt: 1, mb: 0 }}>{t("barometer.nLabel", { n: report.burnout.n })}</Typography>
          </Box>
        )
      )}

      {modules.includes("werkbeleving") && (
        !report.werkbeleving ? <ModuleSuppressedNote k={report.kThreshold} /> : (
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t("barometer.werkReportTitle")}</Typography>
            {/* Transparency: own questions, NOT a validated clinical instrument. */}
            <Typography sx={{ color: "text.secondary", fontSize: ".85rem", fontStyle: "italic", mb: 0.5 }}>{t("barometer.werkDisclaimer")}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: ".9rem", mb: 1.5 }}>{t("barometer.werkHint")}</Typography>
            <Stack spacing={1}>
              {report.werkbeleving.itemMeans.map((m, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ m: 0 }}>{t(`barometer.werk.i${i + 1}`)}</Typography>
                  <Typography sx={{ m: 0, fontWeight: 600 }}>
                    {m}
                    {prev?.werkbeleving && <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 1 }}>({delta(m, prev.werkbeleving.itemMeans[i])})</Box>}
                  </Typography>
                </Box>
              ))}
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ m: 0, fontWeight: 600 }}>{t("barometer.werkOverall")}</Typography>
                <Typography sx={{ m: 0, fontWeight: 700 }}>
                  {report.werkbeleving.overallMean}
                  {prev?.werkbeleving && <Box component="span" sx={{ fontWeight: 400, color: "text.secondary", ml: 1 }}>({delta(report.werkbeleving.overallMean, prev.werkbeleving.overallMean)})</Box>}
                </Typography>
              </Box>
            </Stack>
            <Typography sx={{ color: "text.secondary", fontSize: ".85rem", mt: 1, mb: 0 }}>{t("barometer.nLabel", { n: report.werkbeleving.n })}</Typography>
          </Box>
        )
      )}

      {prev && <Typography sx={{ fontSize: ".85rem", color: "text.secondary", m: 0 }}>{t("barometer.prevNoteModular", { title: prev.title })}</Typography>}

      {/* Instrument-attributie: de BAT-licentie (vrij, ook commercieel) vereist
          correcte citatie — die hoort dus bij elk rapport, ook de afdruk/PDF. */}
      <Box>
        {report.welzijn && <Typography sx={{ fontSize: ".75rem", color: "text.secondary", m: 0 }}>{t("barometer.cite.welzijn")}</Typography>}
        {report.burnout && <Typography sx={{ fontSize: ".75rem", color: "text.secondary", m: 0 }}>{t("barometer.cite.burnout")}</Typography>}
        {report.werkbeleving && <Typography sx={{ fontSize: ".75rem", color: "text.secondary", m: 0 }}>{t("barometer.cite.werkbeleving")}</Typography>}
      </Box>
    </Stack>
  );
}

export default function OrgBarometer() {
  const { t } = useTranslation();
  const [waves, setWaves] = useState<BarometerWaveSummary[] | null>(null);
  const [title, setTitle] = useState("");
  const [modules, setModules] = useState<BarometerModule[]>([...BAROMETER_MODULES]);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [reportId, setReportId] = useState("");
  const [report, setReport] = useState<BarometerReport | null>(null);

  const refresh = () => BarometerApi.listWaves().then(setWaves).catch(() => setWaves([]));
  useEffect(() => { refresh(); }, []);

  const hasOpenWave = (waves || []).some((w) => w.status === "open");

  const toggleModule = (m: BarometerModule) =>
    setModules((cur) => (cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]));

  const create = async () => {
    setErrMsg("");
    setBusy(true);
    try {
      await BarometerApi.createWave(title.trim(), BAROMETER_MODULES.filter((m) => modules.includes(m)));
      setTitle("");
      setModules([...BAROMETER_MODULES]);
      await refresh();
    }
    catch { setErrMsg(t("barometer.createError")); }
    finally { setBusy(false); }
  };
  const close = async (id: string) => {
    setErrMsg("");
    try { await BarometerApi.closeWave(id); await refresh(); }
    catch { setErrMsg(t("barometer.genericError")); }
  };
  const copyLink = async (w: BarometerWaveSummary) => {
    const url = `${window.location.origin}/barometer/${w.shareToken}`;
    try { await navigator.clipboard.writeText(url); setCopiedId(w.id); setTimeout(() => setCopiedId(""), 2500); }
    catch { window.prompt(t("barometer.copyManually"), url); }
  };
  const showReport = async (id: string) => {
    setErrMsg("");
    setReportId(id);
    setReport(null);
    try { setReport(await BarometerApi.report(id)); }
    catch { setErrMsg(t("barometer.genericError")); setReportId(""); }
  };

  if (!waves) return <CircularProgress size={24} sx={{ color: "primary.main" }} />;

  return (
    <Stack spacing={3}>
      {printStyles}
      {/* De tab-intro (DashboardLayout) zegt al wat de barometer is; hier
          alleen nog het praktische: modules kiezen + k-regel. */}
      <Typography sx={{ color: "text.secondary", m: 0 }}>{t("barometer.adminIntroShort", { k: 10 })}</Typography>

      {!hasOpenWave && (
        <Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { xs: "flex-start", sm: "center" } }}>
            <TextField size="small" label={t("barometer.newWaveLabel")} placeholder={t("barometer.newWavePlaceholder")}
              value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} sx={{ minWidth: 240 }} />
            <Button variant="contained" onClick={create} disabled={!title.trim() || modules.length === 0 || busy}>{t("barometer.createWave")}</Button>
          </Stack>
          <Typography sx={{ fontSize: ".9rem", color: "text.secondary", mt: 1.5, mb: 0 }}>{t("barometer.modulesLabel")}</Typography>
          <FormGroup row>
            {BAROMETER_MODULES.map((m) => (
              <FormControlLabel key={m} control={<Checkbox size="small" checked={modules.includes(m)} onChange={() => toggleModule(m)} />}
                label={t(`barometer.module.${m}`)} />
            ))}
          </FormGroup>
          {modules.length === 0 && <Typography sx={{ fontSize: ".85rem", color: "error.main", m: 0 }}>{t("barometer.modulesRequired")}</Typography>}
        </Box>
      )}

      {errMsg && <Alert severity="error" onClose={() => setErrMsg("")}>{errMsg}</Alert>}

      {waves.length === 0 ? (
        <Typography sx={{ color: "text.secondary", m: 0 }}>{t("barometer.noWaves")}</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {waves.map((w) => (
            <Box key={w.id} sx={{ py: 2 }}>
              {/* Vaste rij-structuur per golf: (1) titel + status, (2) module-
                  chips, (3) acties in één knopstijl — rust i.p.v. een wrap-rij
                  waarin titel, chips en drie knopstijlen door elkaar vallen. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontWeight: 600, m: 0, flex: 1 }}>{w.title}</Typography>
                <Chip size="small" label={w.status === "open" ? t("barometer.statusOpen") : t("barometer.statusClosed")}
                  color={w.status === "open" ? "success" : "default"} />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                {(w.modules || []).map((m) => (
                  <Chip key={m} size="small" variant="outlined" label={t(`barometer.module.${m}`)} />
                ))}
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                {w.status === "open" && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={() => copyLink(w)}>
                      {copiedId === w.id ? t("barometer.copied") : t("barometer.copyLink")}
                    </Button>
                    <Button size="small" variant="outlined" color="warning" onClick={() => close(w.id)}>{t("barometer.closeWave")}</Button>
                  </>
                )}
                <Button size="small" variant="outlined" onClick={() => showReport(w.id)}>{t("barometer.viewReport")}</Button>
              </Stack>
              {w.status === "open" && <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mt: 1, mb: 0 }}>{t("barometer.shareHint")}</Typography>}
              {reportId === w.id && (
                <Box sx={{ mt: 2 }}>
                  {report ? (
                    <>
                      {/* Export (manual §3.3): print/PDF + CSV van exact hetzelfde k-veilige aggregaat. */}
                      {!report.suppressed && (
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} className="baro-no-print">
                          <Button size="small" variant="outlined" startIcon={<PrintRoundedIcon />} onClick={() => window.print()}>{t("barometer.printReport")}</Button>
                          <Button size="small" variant="outlined" startIcon={<FileDownloadRoundedIcon />}
                            onClick={() => BarometerApi.downloadReportCsv(w.id).catch(() => setErrMsg(t("barometer.genericError")))}>
                            {t("barometer.downloadCsv")}
                          </Button>
                        </Stack>
                      )}
                      <Box className="baro-print">
                        <Typography sx={{ display: "none", "@media print": { display: "block" }, fontWeight: 700, fontSize: "1.2rem", mb: 2 }}>
                          {t("barometer.printTitle", { title: w.title })}
                        </Typography>
                        <ReportView report={report} />
                      </Box>
                    </>
                  ) : <CircularProgress size={20} sx={{ color: "primary.main" }} />}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
